import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { Wallet, RefreshCw, TrendingUp, DollarSign, ArrowRightLeft, Calendar, ArrowUpRight, Package, CreditCard, ArrowDownLeft } from 'lucide-react';

const AerovoltWallet = () => {
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({
    totalCredits: 0,
    totalDebits: 0,
    totalTransferred: 0,
    adminAdded: 0,
    transferCount: 0,
    totalPackagePurchases: 0,
    packageCount: 0,
    returnCount: 0,
    totalReturns: 0,
    netBalance: 0
  });

  // Get all necessary user identifiers from localStorage
  const userDbKey = localStorage.getItem('dbKey');
  const userRegistrationDate = localStorage.getItem('registrationDate');
  const userId = localStorage.getItem('userId');
  const userSponsorId = localStorage.getItem('userSponsorId');

  useEffect(() => {
    if (userDbKey && userRegistrationDate) {
      fetchWalletData();
    }
  }, [userDbKey, userRegistrationDate, userId, userSponsorId]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      
      // ============================================
      // 1. FETCH CURRENT WALLET BALANCE
      // ============================================
      let currentBalance = 0;
      const userRef = ref(database, `users/${userRegistrationDate}/${userDbKey}`);
      const userSnapshot = await get(userRef);
      
      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        currentBalance = parseFloat(userData.wallets?.aurum) || 0;
        setWalletBalance(currentBalance);
      }
      
      const transactionsList = [];
      let totalCredits = 0; // Money coming IN
      let totalDebits = 0;  // Money going OUT
      let totalTransferred = 0; 
      let adminAdded = 0;
      let transferCount = 0;
      let totalPackagePurchases = 0;
      let packageCount = 0;
      let totalReturns = 0;
      let returnCount = 0;
      
      // Get the correct user identifier to search for
      const currentUserId = userSponsorId || userId || userDbKey;
      console.log('Filtering transactions for user:', currentUserId);
      
      // ============================================
      // 2. FETCH ADMIN ADDED HISTORY (Credits from Aerovolt)
      // ============================================
      try {
        const adminHistoryRef = ref(database, 'adminaddedhistory');
        const adminHistorySnapshot = await get(adminHistoryRef);
        
        if (adminHistorySnapshot.exists()) {
          const data = adminHistorySnapshot.val();
          
          // Iterate through dates
          Object.entries(data).forEach(([dateKey, dateData]) => {
            // Iterate through sponsors under each date
            Object.entries(dateData).forEach(([sponsorId, sponsorTransactions]) => {
              // CRITICAL: Check if this sponsor ID matches the current user ONLY
              if (sponsorId !== currentUserId) {
                return; // Skip if not this user's transactions
              }
              
              // Process transactions for this user
              if (Array.isArray(sponsorTransactions)) {
                sponsorTransactions.forEach((tx, index) => {
                  // Check if this is AV Wallet transaction
                  const isAGWallet = (
                    tx.walletType === 'AV Wallet' || 
                    tx.walletType === 'aurum' ||
                    tx.transactionType === 'Add AV Wallet' ||
                    tx.wallet === 'aurum'
                  );
                  
                  if (isAGWallet) {
                    const amount = parseFloat(tx.amount) || 0;
                    
                    // Determine if this is a credit or debit
                    const isDebit = amount < 0 || 
                                   tx.transactionType?.toLowerCase().includes('debit') ||
                                   tx.description?.toLowerCase().includes('return') ||
                                   tx.description?.toLowerCase().includes('debit');
                    
                    const transaction = {
                      id: tx.transactionId || `admin_${dateKey}_${sponsorId}_${index}`,
                      date: dateKey,
                      amount: Math.abs(amount),
                      rawAmount: amount,
                      type: isDebit ? 'admin_debit' : 'admin_added',
                      status: 'completed',
                      previousBalance: parseFloat(tx.previousBalance) || 0,
                      newBalance: parseFloat(tx.newBalance) || 0,
                      description: tx.description || tx.transactionType || (isDebit ? 'Admin debit' : 'Admin credited funds'),
                      timestamp: tx.date || dateKey,
                      addedBy: 'Aerovolt',
                      walletType: tx.walletType,
                      source: 'Aerovolt',
                      isAdminAdded: !isDebit,
                      isAdminDebit: isDebit,
                      displayType: isDebit ? 'Admin Debit / Return' : 'Admin Added',
                      transactionType: isDebit ? 'debit' : 'credit'
                    };
                    
                    transactionsList.push(transaction);
                    
                    if (isDebit) {
                      totalDebits += Math.abs(amount);
                      if (tx.description?.toLowerCase().includes('return')) {
                        totalReturns += Math.abs(amount);
                        returnCount++;
                      }
                    } else {
                      totalCredits += amount;
                      adminAdded += amount;
                    }
                  }
                });
              } else if (sponsorTransactions && typeof sponsorTransactions === 'object') {
                // If it's a single transaction object
                const tx = sponsorTransactions;
                
                const isAGWallet = (
                  tx.walletType === 'AV Wallet' || 
                  tx.walletType === 'aurum' ||
                  tx.transactionType === 'Add AV Wallet' ||
                  tx.wallet === 'aurum'
                );
                
                if (isAGWallet) {
                  const amount = parseFloat(tx.amount) || 0;
                  
                  // Determine if this is a credit or debit
                  const isDebit = amount < 0 || 
                                 tx.transactionType?.toLowerCase().includes('debit') ||
                                 tx.description?.toLowerCase().includes('return') ||
                                 tx.description?.toLowerCase().includes('debit');
                  
                  const transaction = {
                    id: tx.transactionId || `admin_${dateKey}_${sponsorId}`,
                    date: dateKey,
                    amount: Math.abs(amount),
                    rawAmount: amount,
                    type: isDebit ? 'admin_debit' : 'admin_added',
                    status: 'completed',
                    previousBalance: parseFloat(tx.previousBalance) || 0,
                    newBalance: parseFloat(tx.newBalance) || 0,
                    description: tx.description || tx.transactionType || (isDebit ? 'Admin debit' : 'Admin credited funds'),
                    timestamp: tx.date || dateKey,
                    addedBy: 'Aerovolt',
                    walletType: tx.walletType,
                    source: 'Aerovolt',
                    isAdminAdded: !isDebit,
                    isAdminDebit: isDebit,
                    displayType: isDebit ? 'Admin Debit / Return' : 'Admin Added',
                    transactionType: isDebit ? 'debit' : 'credit'
                  };
                  
                  transactionsList.push(transaction);
                  
                  if (isDebit) {
                    totalDebits += Math.abs(amount);
                    if (tx.description?.toLowerCase().includes('return')) {
                      totalReturns += Math.abs(amount);
                      returnCount++;
                    }
                  } else {
                    totalCredits += amount;
                    adminAdded += amount;
                  }
                }
              }
            });
          });
        }
      } catch (error) {
        console.error('Error fetching adminaddedhistory:', error);
      }
      
      // ============================================
      // 3. FETCH TRANSFERS INTO ASTRAGRID WALLET
      // ============================================
      try {
        const transfersRef = ref(database, 'Transfers');
        const transfersSnapshot = await get(transfersRef);
        
        if (transfersSnapshot.exists()) {
          const data = transfersSnapshot.val();
          
          // Iterate through dates
          Object.entries(data).forEach(([dateKey, dateData]) => {
            // CRITICAL: Check if this user has transfers on this date
            if (dateData[currentUserId]) {
              const userTransfers = dateData[currentUserId];
              
              Object.entries(userTransfers).forEach(([transferId, transfer]) => {
                // Check if transfer is TO aurum wallet (Aerovolt Wallet)
                if (transfer.toWallet === 'aurum') {
                  const amount = parseFloat(transfer.amount) || 0;
                  
                  const transaction = {
                    id: transferId,
                    date: dateKey,
                    amount: amount,
                    type: 'transfer_in',
                    status: transfer.status || 'completed',
                    previousBalance: parseFloat(transfer.previousToBalance) || 0,
                    newBalance: parseFloat(transfer.newToBalance) || 0,
                    description: `Transferred from ${transfer.fromWallet}`,
                    timestamp: transfer.timestamp || transfer.date || dateKey,
                    fromWallet: transfer.fromWallet,
                    toWallet: transfer.toWallet,
                    source: 'Transfer',
                    displayType: 'Transfer In',
                    fromWalletLabel: getWalletLabel(transfer.fromWallet),
                    transactionType: 'credit'
                  };
                  
                  transactionsList.push(transaction);
                  totalCredits += amount;
                  totalTransferred += amount;
                  transferCount++;
                }
              });
            }
          });
        }
      } catch (error) {
        console.error('Error fetching transfers:', error);
      }
      
      // ============================================
      // 4. FETCH PACKAGE PURCHASES FROM ASTRAGRID WALLET
      // ============================================
      try {
        // Check PackageActivations for purchases made with aurum wallet
        const packageActivationsRef = ref(database, 'PackageActivations');
        const packageSnapshot = await get(packageActivationsRef);
        
        if (packageSnapshot.exists()) {
          const data = packageSnapshot.val();
          
          // Iterate through dates
          Object.entries(data).forEach(([dateKey, dateData]) => {
            // Iterate through users
            Object.entries(dateData).forEach(([userKey, userPackages]) => {
              // CRITICAL: Check if this user ID matches current user ONLY
              if (userKey !== currentUserId && userKey !== userId && userKey !== userSponsorId) {
                return; // Skip if not this user
              }
              
              // Process each package for this user
              Object.entries(userPackages).forEach(([pkgId, pkgData]) => {
                // Check if package was purchased using aurum wallet
                const walletUsed = pkgData.walletUsed || pkgData.paymentWallet || '';
                const isFromAurum = (
                  walletUsed === 'aurum' || 
                  walletUsed === 'Aerovolt' || 
                  walletUsed === 'Aerovolt Wallet' ||
                  pkgData.walletUsed === 'aurum'
                );
                
                if (isFromAurum) {
                  const amount = parseFloat(pkgData.amount) || parseFloat(pkgData.investmentAmount) || 0;
                  
                  if (amount > 0) {
                    const transaction = {
                      id: pkgId,
                      date: dateKey,
                      amount: amount,
                      type: 'package_purchase',
                      status: 'completed',
                      previousBalance: parseFloat(pkgData.previousAurumBalance) || 0,
                      newBalance: parseFloat(pkgData.newAurumBalance) || 0,
                      description: `Package Purchase: ${pkgData.packageName || 'Package'}`,
                      timestamp: pkgData.timestamp || pkgData.purchaseDate || dateKey,
                      packageName: pkgData.packageName || 'Package',
                      packageId: pkgData.packageId,
                      source: 'Package Purchase',
                      displayType: 'Package Purchase',
                      transactionType: 'debit',
                      isPackagePurchase: true
                    };
                    
                    transactionsList.push(transaction);
                    totalDebits += amount;
                    totalPackagePurchases += amount;
                    packageCount++;
                  }
                }
              });
            });
          });
        }
        
        // Also check user's own packages array for purchases made with aurum
        const userPackagesRef = ref(database, `users/${userRegistrationDate}/${userDbKey}/packages`);
        const userPackagesSnapshot = await get(userPackagesRef);
        
        if (userPackagesSnapshot.exists()) {
          const userPackages = userPackagesSnapshot.val();
          
          // Handle both array and object formats
          const packagesArray = Array.isArray(userPackages) ? userPackages : Object.values(userPackages);
          
          packagesArray.forEach((pkg, index) => {
            // Check if purchased with aurum wallet
            const walletUsed = pkg.walletUsed || pkg.paymentWallet || '';
            const isFromAurum = (
              walletUsed === 'aurum' || 
              walletUsed === 'Aerovolt' || 
              walletUsed === 'Aerovolt Wallet' ||
              pkg.walletUsed === 'aurum'
            );
            
            if (isFromAurum) {
              const amount = parseFloat(pkg.investmentAmount) || parseFloat(pkg.amount) || 0;
              
              if (amount > 0) {
                // Check if we already have this transaction from PackageActivations
                const existingTransaction = transactionsList.find(tx => 
                  tx.id === pkg.transactionId || 
                  (tx.description && tx.description.includes(pkg.packageName) && 
                   Math.abs(tx.amount - amount) < 0.01)
                );
                
                if (!existingTransaction) {
                  const transaction = {
                    id: pkg.transactionId || `package_user_${Date.now()}_${index}`,
                    date: pkg.purchaseDate || new Date().toISOString(),
                    amount: amount,
                    type: 'package_purchase',
                    status: 'completed',
                    previousBalance: 0,
                    newBalance: 0,
                    description: `Package Purchase: ${pkg.packageName || 'Package'}`,
                    timestamp: pkg.purchaseDate || new Date().toISOString(),
                    packageName: pkg.packageName || 'Package',
                    packageId: pkg.packageId,
                    source: 'Package Purchase',
                    displayType: 'Package Purchase',
                    transactionType: 'debit',
                    isPackagePurchase: true
                  };
                  
                  transactionsList.push(transaction);
                  totalDebits += amount;
                  totalPackagePurchases += amount;
                  packageCount++;
                }
              }
            }
          });
        }
        
      } catch (error) {
        console.error('Error fetching package purchases:', error);
      }
      
      // ============================================
      // 5. REMOVE DUPLICATES AND CALCULATE STATS
      // ============================================
      
      // Sort by date (newest first)
      transactionsList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      // Remove duplicates based on ID
      const uniqueTransactions = [];
      const seenIds = new Set();
      
      transactionsList.forEach(tx => {
        if (!seenIds.has(tx.id)) {
          seenIds.add(tx.id);
          uniqueTransactions.push(tx);
        }
      });
      
      console.log(`Found ${uniqueTransactions.length} transactions for user ${currentUserId}`);
      console.log(`  - Credits: ${totalCredits}`);
      console.log(`  - Debits: ${totalDebits}`);
      console.log(`  - Returns: ${totalReturns}`);
      
      setTransactions(uniqueTransactions);
      setStats({
        totalCredits,
        totalDebits,
        totalTransferred,
        adminAdded,
        transferCount,
        totalPackagePurchases,
        packageCount,
        totalReturns,
        returnCount,
        netBalance: totalCredits - totalDebits
      });
      setLastUpdated(new Date().toLocaleTimeString());
      
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWalletLabel = (walletKey) => {
    const map = {
      working: "AV Power Wallet",
      roi: "AV ROI Wallet",
      energy: "AV Energy Wallet",
      aurum: "Aerovolt Wallet",
      fund: "AV PAY Wallet"
    };
    return map[walletKey] || walletKey;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getTransactionIcon = (transaction) => {
    if (transaction.isAdminDebit) {
      return <ArrowDownLeft className="h-5 w-5 text-red-400" />;
    }
    if (transaction.isAdminAdded) {
      return <ArrowUpRight className="h-5 w-5 text-purple-400" />;
    }
    if (transaction.type === 'transfer_in') {
      return <ArrowRightLeft className="h-5 w-5 text-blue-400" />;
    }
    if (transaction.isPackagePurchase || transaction.type === 'package_purchase') {
      return <Package className="h-5 w-5 text-orange-400" />;
    }
    return <DollarSign className="h-5 w-5 text-green-400" />;
  };

  const getTransactionColor = (transaction) => {
    if (transaction.isAdminDebit) return 'bg-red-500/20';
    if (transaction.isAdminAdded) return 'bg-purple-500/20';
    if (transaction.type === 'transfer_in') return 'bg-blue-500/20';
    if (transaction.isPackagePurchase || transaction.type === 'package_purchase') return 'bg-orange-500/20';
    return 'bg-green-500/20';
  };

  const getAmountColor = (transaction) => {
    if (transaction.transactionType === 'debit' || transaction.isAdminDebit) {
      return 'text-red-400';
    }
    return 'text-green-400';
  };

  const getAmountPrefix = (transaction) => {
    if (transaction.transactionType === 'debit' || transaction.isAdminDebit) {
      return '-';
    }
    return '+';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">Aerovolt Wallet</h1>
      <p className="text-gray-400 mb-6">Your main investment wallet balance</p>

      {/* Wallet Balance Card */}
      <div className="card-glow p-8 mb-8 bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/30 rounded-2xl">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="text-center md:text-left mb-6 md:mb-0">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-primary/20 rounded-full">
                <Wallet className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Aerovolt Wallet</h2>
                <p className="text-gray-400 text-sm">Investment & Package Purchases</p>
              </div>
            </div>
            
            <div className="mt-4">
              <div className="text-gray-400 text-sm mb-1">Current Balance</div>
              <div className="text-5xl font-bold text-white">{formatCurrency(walletBalance)}</div>
            </div>
            
            {lastUpdated && (
              <div className="text-gray-500 text-xs mt-2">
                Last updated: {lastUpdated}
              </div>
            )}
          </div>
          
          <div className="flex flex-col gap-3">
            <button
              onClick={fetchWalletData}
              className="px-6 py-3 bg-primary hover:bg-primary/80 rounded-xl text-white font-bold flex items-center gap-2"
            >
              <RefreshCw className="h-5 w-5" />
              Refresh Balance
            </button>
            
            <button
              onClick={() => window.location.href = '/user-dashboard/activate'}
              className="px-6 py-3 bg-green-500 hover:bg-green-600 rounded-xl text-white font-bold flex items-center gap-2"
            >
              <TrendingUp className="h-5 w-5" />
              Buy Packages
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards - Only showing current user's data */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Current Balance</div>
          <div className="text-lg font-bold text-primary">{formatCurrency(walletBalance)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Credits</div>
          <div className="text-lg font-bold text-green-400">{formatCurrency(stats.totalCredits)}</div>
          <div className="text-xs text-gray-400">Money received</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Debits</div>
          <div className="text-lg font-bold text-red-400">{formatCurrency(stats.totalDebits)}</div>
          <div className="text-xs text-gray-400">Money spent</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Net Balance</div>
          <div className={`text-lg font-bold ${stats.netBalance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {formatCurrency(stats.netBalance)}
          </div>
          <div className="text-xs text-gray-400">Credits - Debits</div>
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Admin Credits</div>
          <div className="text-lg font-bold text-purple-400">{formatCurrency(stats.adminAdded)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Admin Debits/Returns</div>
          <div className="text-lg font-bold text-red-400">{formatCurrency(stats.totalReturns)}</div>
          <div className="text-xs text-gray-400">{stats.returnCount} returns</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Transfers In</div>
          <div className="text-lg font-bold text-blue-400">{formatCurrency(stats.totalTransferred)}</div>
          <div className="text-xs text-gray-400">{stats.transferCount} transfers</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Package Purchases</div>
          <div className="text-lg font-bold text-orange-400">{formatCurrency(stats.totalPackagePurchases)}</div>
          <div className="text-xs text-gray-400">{stats.packageCount} packages</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">User ID</div>
          <div className="text-sm font-bold text-white truncate">{userSponsorId || userId}</div>
        </div>
      </div>

      {/* Transaction History - Only current user's transactions */}
      <div className="card-glow p-6 bg-gray-800/50 rounded-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-white">Your Transaction History</h3>
          <button
            onClick={fetchWalletData}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            Refresh
          </button>
        </div>
        
        {transactions.length > 0 ? (
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="p-4 bg-gray-900/50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${getTransactionColor(transaction)}`}>
                      {getTransactionIcon(transaction)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {transaction.isAdminAdded && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            Admin Credit
                          </span>
                        )}
                        {transaction.isAdminDebit && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400 border border-red-500/30">
                            Admin Debit / Return
                          </span>
                        )}
                        {transaction.type === 'transfer_in' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            Transfer In
                          </span>
                        )}
                        {transaction.isPackagePurchase && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30">
                            Package Purchase
                          </span>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm">
                        {transaction.description || 'Wallet Transaction'}
                      </p>
                      {transaction.packageName && (
                        <p className="text-primary text-xs mt-1">
                          Package: {transaction.packageName}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-gray-400 text-xs flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(transaction.timestamp)}
                        </span>
                        {transaction.fromWallet && (
                          <span>From: {transaction.fromWalletLabel}</span>
                        )}
                        {transaction.walletType && (
                          <span>Wallet: {transaction.walletType}</span>
                        )}
                      </div>
                      {transaction.previousBalance > 0 && (
                        <p className="text-gray-500 text-xs mt-1">
                          Balance: {formatCurrency(transaction.previousBalance)} → {formatCurrency(transaction.newBalance)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={`${getAmountColor(transaction)} font-bold text-right min-w-[100px]`}>
                    {getAmountPrefix(transaction)}{formatCurrency(transaction.amount)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Transactions</h3>
            <p className="text-gray-400">No transaction history found for your Aerovolt Wallet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AerovoltWallet;

